import React, { JSX } from "react";
import { ReactFlow, ReactFlowInstance, Background, Controls, MiniMap, Node as RFNode, Edge as RFEdge, applyNodeChanges, applyEdgeChanges, OnNodesChange, OnEdgesChange, Connection, addEdge, OnConnect, OnConnectStart, Handle, Position, MarkerType, NodeProps, EdgeProps, getBezierPath, BaseEdge, EdgeLabelRenderer, Panel, EdgeToolbar } from "@xyflow/react";
// @ts-ignore
import "@xyflow/react/dist/style.css";
import dagre from "@dagrejs/dagre";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { ProgressIndicatorIndeterminate } from "@serendie/ui";
import { appContext } from "../../../../AppContext";
import { SerendieSymbol } from "@serendie/symbols";
import { Button, DeleteButton } from "@iconics/components";
import { DirtyPageObserver } from "@iconics/components";
// @ts-ignore
import "./Styles.scss";

export class GraphView extends React.Component<IGraphViewProps, IGraphViewState>
{
    private languageValuesHelper = new LanguageValuesHelper(appContext().localisation);
    private labels = appContext().labels;
    private suppressDirtyTracking = true;
    private reactFlowInstance: ReactFlowInstance<RFNode, RFEdge> | undefined;
    private storedGraphState = '';
    private connectStart?: { nodeId: string | null; handleType: 'source' | 'target' | null };
    private readonly pastelPalette = [
        "#00B894",
        "#0984E3",
        "#6C5CE7",
        "#D63031",
        "#E17055",
        "#00CEC9",
        "#636E72",
        "#FD79A8",
        "#E84393",
        "#2D3436",
    ];
    private readonly nodeHeaderColorNoOntology = "#2D3436";
    private readonly nodeHeaderColorMultiOntology = "#BDBDBD";
    private readonly nodeBgColorDefault = "#FFFFFF";
    private readonly nodeWidth = 240;
    private readonly nodeHeight = 60;
    private readonly hierarchicalMarkerEnd = {
        type: MarkerType.ArrowClosed,
        color: "#FFFFFF",
        width: 7,
    };
    private readonly deletableMarkerEnd = {
        type: MarkerType.ArrowClosed,
        color: "var(--colors-sd-system-color-impression-primary)",
        width: 7,
    };
    private get isInteractionBlocked(): boolean
    {
        // Empty refresh token = parent hasn't finished loading yet.
        return !this.props.refreshToken;
    }

    constructor(props: IGraphViewProps)
    {
        super(props);
        this.state = {
            nodes: [],
            edges: [],
            isDirty: false,
        };
    }

    public async componentDidUpdate(prevProps: IGraphViewProps): Promise<void>
    {
        if (prevProps.rootNodeId !== this.props.rootNodeId ||
            prevProps.storedLayout !== this.props.storedLayout)
        {
            this.restoreGraph(true);
        }

        // syncAndLayout is the heavy "build RFNodes from props + run dagre" step. It
        // runs whenever the parent stamps a new refresh token — that's the explicit
        // signal that a fresh batch of nodes/edges is ready and we should re-build
        // the visual layer. Empty token = still loading, no sync.
        if (prevProps.refreshToken !== this.props.refreshToken)
        {
            this.syncAndLayout();
        }
    }

    private readStoredGraphState = (): IStoredGraphState | undefined =>
    {
        const raw = this.props.storedLayout;

        if (!raw)
        {
            return undefined;
        }

        try
        {
            return JSON.parse(raw) as IStoredGraphState;
        } catch {
            return undefined;
        }
    };

    private storeGraphState = (state: IStoredGraphState): void =>
    {
        // Strip selection before persisting — selection is a runtime concept tied
        // to the user's current focus, not part of the layout. Leaving it in would
        // mean a save+restore round-trip could change which node looks selected.
        const stripped: IStoredGraphState = {
            ...state,
            nodes: state.nodes?.map(n => ({ ...n, selected: false })),
        };
        const json = JSON.stringify(stripped);
        if (json !== this.storedGraphState)
            this.props.onSaveLayout(json);
        this.storedGraphState = json;
    };

    // Non-hierarchical (deletable) edges are derived from props on every sync, so they
    // are excluded from the persisted layout — including them would just bloat the saved
    // state and double-up on data already coming from the backend.
    private toExportableEdges = (edges: RFEdge[]): RFEdge[] =>
    {
        return edges.filter((e) => e.type !== "deletable");
    };

    // Persist the current layout (including the just-added/removed edge) only when there
    // are no pending node-layout changes; otherwise the auto-save would also commit those
    // unsaved layout edits, which the user must still confirm via the Save button.
    private autoSaveLayoutIfClean = (): void =>
    {
        if (this.state.isDirty || !this.reactFlowInstance)
        {
            return;
        }
        this.storeGraphState({
            nodes: this.state.nodes,
            edges: this.toExportableEdges(this.state.edges),
            viewport: this.reactFlowInstance.getViewport(),
        });
    };

    private get singleOntologyPalette(): string[]
    {
        return this.pastelPalette.filter(
            (color) => color !== this.nodeHeaderColorNoOntology,
        );
    }

    private getNodeHeaderColorByOntology = (
        ontologyNodes: IOntologyNode[],
    ): string =>
    {
        if (ontologyNodes.length === 0)
        {
            return this.nodeHeaderColorNoOntology;
        }

        if (ontologyNodes.length > 1)
        {
            return this.nodeHeaderColorMultiOntology;
        }

        const color = ontologyNodes[0].styling["color"];
        if (color)
        {
            return color;
        }

        const ontologyNodeName = ontologyNodes[0].name.toString() ?? "";
        const combinedKey = ontologyNodeName.toLowerCase().trim();
        let hash = 0;

        for (let i = 0; i < combinedKey.length; i++)
        {
            hash = combinedKey.charCodeAt(i) + ((hash << 5) - hash);
            hash = hash & hash;
        }

        const index = Math.abs(hash) % this.singleOntologyPalette.length;
        return this.singleOntologyPalette[index];
    };

    private getNodeBgColorByOntology = (
        ontologyNodes: IOntologyNode[],
    ): string =>
    {
        if (ontologyNodes.length === 0 || ontologyNodes.length > 1)
        {
            return this.nodeBgColorDefault;
        }

        const color = ontologyNodes[0].styling["background-color"];
        if (color)
        {
            return color;
        }
        return this.nodeBgColorDefault;
    };

    private getLayoutedElements = (nodes: RFNode[], edges: RFEdge[]) =>
    {
        const dagreGraph = new dagre.graphlib.Graph();
        dagreGraph.setDefaultEdgeLabel(() => ({}));
        dagreGraph.setGraph({
            rankdir: "TB",
            ranksep: 250,
            nodesep: 150,
            marginx: 50,
            marginy: 50,
        });

        nodes.forEach((node) =>
        {
            dagreGraph.setNode(node.id, {
                width: this.nodeWidth,
                height: this.nodeHeight,
            });
        });

        edges.forEach((edge) =>
        {
            dagreGraph.setEdge(edge.source, edge.target);
        });

        dagre.layout(dagreGraph);

        const layoutedNodes = nodes.map((node) =>
        {
            const nodeWithPosition = dagreGraph.node(node.id);
            return {
                ...node,
                position: {
                    x: nodeWithPosition.x - this.nodeWidth / 2,
                    y: nodeWithPosition.y - this.nodeHeight / 2,
                },
            };
        });

        return { nodes: layoutedNodes, edges };
    };

    private hasMeaningfulNodeChanges = (
        changes: Parameters<OnNodesChange>[0],
    ): boolean =>
    {
        return changes.some((change) =>
        {
            if (
                change.type === "add" ||
                change.type === "remove" ||
                change.type === "replace"
            ) {
                return true;
            }

            if (change.type === "position")
            {
                // Ignore non-user position recalculations on initial render.
                return "dragging" in change;
            }

            return false;
        });
    };

private getVisibleItemsForTwoLines = (
        items: string[],
        maxCharsPerLine = 26,
    ): { visibleItems: string[]; hiddenItems: string[] } =>
    {
        const normalizedItems = items
            .map((item) => (item ?? "").trim())
            .filter((item) => !!item);

        if (normalizedItems.length === 0)
        {
            return { visibleItems: [], hiddenItems: [] };
        }

        const toChipChars = (value: string): number =>
            Math.min(value.length + 2, maxCharsPerLine);
        const truncateToChipChars = (value: string, chipChars: number): string =>
        {
            const maxContentChars = Math.max(1, chipChars - 2);
            if (value.length <= maxContentChars)
            {
                return value;
            }

            if (maxContentChars <= 3)
            {
                return ".".repeat(maxContentChars);
            }

            return `${value.slice(0, maxContentChars - 3)}...`;
        };

        const visibleItems: string[] = [];
        let line1Chars = 0;
        let line2Chars = 0;
        let isSecondLine = false;
        let secondLineStartIndex = 0;

        for (const item of normalizedItems)
        {
            const chipChars = toChipChars(item);

            if (!isSecondLine)
            {
                const fitsLine1 =
                    line1Chars === 0 || line1Chars + chipChars <= maxCharsPerLine;
                if (fitsLine1)
                {
                    visibleItems.push(item);
                    line1Chars += chipChars;
                    continue;
                }

                isSecondLine = true;
                secondLineStartIndex = visibleItems.length;
                visibleItems.push(item);
                line2Chars = chipChars;
                continue;
            }

            const fitsLine2 = line2Chars + chipChars <= maxCharsPerLine;
            if (fitsLine2)
            {
                visibleItems.push(item);
                line2Chars += chipChars;
                continue;
            }

            break;
        }

        const hiddenItems = normalizedItems.slice(visibleItems.length);
        if (hiddenItems.length === 0)
        {
            return { visibleItems, hiddenItems: [] };
        }

        const ellipsisChipChars = 5;
        while (line2Chars + ellipsisChipChars > maxCharsPerLine)
        {
            if (visibleItems.length <= secondLineStartIndex)
            {
                break;
            }

            const lastVisibleIndex = visibleItems.length - 1;
            const lastVisibleItem = visibleItems[lastVisibleIndex];
            const lastVisibleChipChars = toChipChars(lastVisibleItem);
            const usedWithoutLast = line2Chars - lastVisibleChipChars;
            const availableChipChars =
                maxCharsPerLine - usedWithoutLast - ellipsisChipChars;

            if (availableChipChars >= 4)
            {
                const truncated = truncateToChipChars(
                    lastVisibleItem,
                    availableChipChars,
                );
                visibleItems[lastVisibleIndex] = truncated;
                line2Chars = usedWithoutLast + toChipChars(truncated);
                break;
            }

            hiddenItems.unshift(lastVisibleItem);
            visibleItems.pop();
            line2Chars = usedWithoutLast;
        }

        return {
            visibleItems,
            hiddenItems,
        };
    };

    private CollapsibleNode = ({
        id,
        data,
        selected,
    }: NodeProps<RFNode<IGraphNodeData>>) =>
    {
        const isExpanded = data.isExpanded ?? true;
        const nodeBorderColor = selected
            ? "#1a73e8"
            : data.headerColor || "#1a73e8";
        const nodeBgColor = data.bgColor || "#FFFFFF";
        const solutionsDisplay = this.getVisibleItemsForTwoLines(
            data.solutions ?? [],
        );
        const ontologyDisplay = this.getVisibleItemsForTwoLines(
            data.ontologyNodes?.map((n) => n.name) ?? [],
        );

        return (
            <div className="graph-node">
                <div
                    className={`graph-node__card ${selected ? "graph-node__card--selected" : ""}`.trim()}
                    style={{
                        ["--graph-node-border-color" as string]: nodeBorderColor,
                        ["--graph-node-bg-color" as string]: nodeBgColor,
                        ["--graph-node-width" as string]: `${this.nodeWidth}px`,
                    } as React.CSSProperties}
                >
                    <div
                        className="graph-node__header"
                        style={{
                            ["--graph-node-header-bg" as string]: data.headerColor || "#1a73e8",
                        } as React.CSSProperties}
                    >
                        <div className="graph-node__title-wrap">
                            <span className="graph-node__title">
                                {data.label}
                            </span>
                        </div>
                        <button
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                            {
                                event.stopPropagation();
                                data.onToggleExpanded?.(id);
                            }}
                            className="graph-node__toggle"
                        >
                            {
                                <SerendieSymbol
                                    name={isExpanded ? "chevron-down" : "chevron-left"}
                                />
                            }
                        </button>
                    </div>

                    {isExpanded && (
                        <>
                            <div
                                className={`graph-node__status ${data.isEnabled ? "graph-node__status--enabled" : "graph-node__status--disabled"}`}
                            >
                                <span className="graph-node__status-dot" />
                                <span className="graph-node__status-text">
                                    {data.isEnabled ? "Enabled" : "Disabled"}
                                </span>
                            </div>
                            <div className="graph-node__body">
                                {solutionsDisplay.visibleItems.length > 0 && (
                                    <div className="graph-node__section">
                                        <div className="graph-node__chip-list">
                                            {solutionsDisplay.visibleItems.map((node: string) => (
                                                <div
                                                    key={node}
                                                    className="graph-node__chip graph-node__chip--solution"
                                                    title={node}
                                                >
                                                    <span className="graph-node__chip-text">{node}</span>
                                                </div>
                                            ))}
                                            {solutionsDisplay.hiddenItems.length > 0 && (
                                                <div
                                                    key="solutions-more"
                                                    className="graph-node__chip graph-node__chip--solution graph-node__chip--more"
                                                    title={solutionsDisplay.hiddenItems.join(", ")}
                                                >
                                                    <span className="graph-node__chip-text">...</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {ontologyDisplay.visibleItems.length > 0 && (
                                    <div className="graph-node__section">
                                        <div className="graph-node__chip-list">
                                            {ontologyDisplay.visibleItems.map((node: string) => (
                                                <div
                                                    key={node}
                                                    className="graph-node__chip graph-node__chip--ontology"
                                                    title={node}
                                                >
                                                    <span className="graph-node__chip-text">{node}</span>
                                                </div>
                                            ))}
                                            {ontologyDisplay.hiddenItems.length > 0 && (
                                                <div
                                                    key="ontology-more"
                                                    className="graph-node__chip graph-node__chip--ontology graph-node__chip--more"
                                                    title={ontologyDisplay.hiddenItems.join(", ")}
                                                >
                                                    <span className="graph-node__chip-text">...</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
                <Handle
                    type="target"
                    position={Position.Top}
                    id="h-target"
                    className="graph-node__handle graph-node__handle--rel"
                />
                <Handle
                    type="source"
                    position={Position.Top}
                    id="top-source"
                    className="graph-node__handle graph-node__handle--rel graph-node__handle--passive"
                />
                <Handle
                    type="source"
                    position={Position.Bottom}
                    id="h-source"
                    className="graph-node__handle graph-node__handle--rel"
                />
                <Handle
                    type="target"
                    position={Position.Bottom}
                    id="bottom-target"
                    className="graph-node__handle graph-node__handle--rel graph-node__handle--passive"
                />
                <Handle
                    type="target"
                    position={Position.Left}
                    id="r-target"
                    className="graph-node__handle graph-node__handle--rel"
                />
                <Handle
                    type="source"
                    position={Position.Left}
                    id="left-source"
                    className="graph-node__handle graph-node__handle--rel graph-node__handle--passive"
                />
                <Handle
                    type="source"
                    position={Position.Right}
                    id="l-source"
                    className="graph-node__handle graph-node__handle--rel"
                />
                <Handle
                    type="target"
                    position={Position.Right}
                    id="right-target"
                    className="graph-node__handle graph-node__handle--rel graph-node__handle--passive"
                />
            </div>
        );
    };

    private DeletableEdge = ({
        id,
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        style = {},
        markerEnd,
        selected,
        data,
    }: EdgeProps<RFEdge>) =>
    {
        const edgeData = (data ?? {}) as {
            label?: string;
            onDelete?: (edgeId: string) => Promise<void> | void;
            // Optional per-edge colour overrides derived from IGraphEdge.styling.
            // labelColor drives the label text colour; labelBgColor drives the
            // label background. Both default (when undefined) to the theme CSS
            // variables declared in Styles.scss.
            labelColor?: string;
            labelBgColor?: string;
        };
        const canDeleteEdge = !!edgeData.onDelete;
        const isInteractionDisabled = !this.props.refreshToken;
        const [edgePath, labelX, labelY] = getBezierPath({
            sourceX,
            sourceY,
            sourcePosition,
            targetX,
            targetY,
            targetPosition,
        });

        return (
            <>
                <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
                <EdgeToolbar
                    edgeId={id}
                    x={labelX}
                    y={labelY - 25}
                    alignY="bottom"
                    isVisible={selected && !isInteractionDisabled && canDeleteEdge}
                >
                    <div className="graph-view__floating-toolbar nodrag nopan">
                        <DeleteButton
                            onDelete={() => edgeData.onDelete?.(id) ?? Promise.resolve()}
                            small
                        />
                    </div>
                </EdgeToolbar>
                <EdgeLabelRenderer>
                    <div
                        style={{
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                        }}
                        className="graph-edge__label-anchor nodrag nopan"
                    >
                        <div className="graph-edge__label-wrap">
                            <span
                                className="graph-edge__label"
                                style={{
                                    ...(edgeData.labelColor
                                        ? { color: edgeData.labelColor }
                                        : {}),
                                    ...(edgeData.labelBgColor
                                        ? { background: edgeData.labelBgColor }
                                        : {}),
                                }}
                            >
                                {edgeData.label as string}
                            </span>
                        </div>
                    </div>
                </EdgeLabelRenderer>
            </>
        );
    };

    private nodeTypes = {
        collapsible: this.CollapsibleNode,
    };

    private edgeTypes = {
        deletable: this.DeletableEdge,
    };

    private withEdgeShadow = (
        style?: React.CSSProperties,
    ): React.CSSProperties =>
    {
        return {
            ...(style ?? {}),
            filter: "drop-shadow(0 0 2px rgba(0, 0, 0, 0.08))",
        };
    };

    private syncAndLayout = () =>
    {
        this.suppressDirtyTracking = true;

        // Build an id → ontology-node lookup once per sync so we can hydrate each
        // node's ontologyNodeIds into the full IOntologyNode objects the card and
        // colour helpers need.
        const ontologyById = new Map(
            this.props.ontologyNodes.map((o) => [o.id, o]),
        );
        const resolveOntologyNodes = (ids: string[]): IOntologyNode[] =>
            ids
                .map((id) => ontologyById.get(id))
                .filter((o): o is IOntologyNode => o !== undefined);

        const initialNodes: RFNode[] = this.props.nodes.map((n) =>
        {
            let name = this.languageValuesHelper.getValue(n.displayName);
            if (!name) name = n.name;
            const ontologyNodes = resolveOntologyNodes(n.ontologyNodeIds);
            return {
                id: n.id.toString(),
                type: "collapsible",
                data: {
                    label: name,
                    isEnabled: n.isEnabled,
                    headerColor: this.getNodeHeaderColorByOntology(ontologyNodes),
                    bgColor: this.getNodeBgColorByOntology(ontologyNodes),
                    solutions: n.solutionNames,
                    ontologyNodes: ontologyNodes,
                    isExpanded: true,
                    onToggleExpanded: this.handleToggleNodeExpanded,
                },
                position: { x: 0, y: 0 },
            };
        });
        const treeEdges: RFEdge[] = [];
        const floatingEdges: RFEdge[] = [];

        this.props.edges.forEach((r) =>
        {
            if (!r.source || !r.destination) return;
            let name = this.languageValuesHelper.getValue(r.displayName);
            if (!name) name = r.name;

            const edge: RFEdge = {
                id: r.id,
                source: r.source.id.toString(),
                target: r.destination.id.toString(),
                label: name,
                data: {
                    label: name,
                },
            };

            if (r.hierarchical)
            {
                edge.type = "smoothstep";
                edge.style = this.withEdgeShadow({ stroke: "#FFFFFF", strokeWidth: 5 });
                edge.markerEnd = { ...this.hierarchicalMarkerEnd };
                edge.sourceHandle = "h-source";
                edge.targetHandle = "h-target";
                treeEdges.push(edge);
            }
            else
            {
                // Non-hierarchical edges default to the theme colours but let the
                // relationship's own styling override them: "background-color" drives
                // the edge body (stroke), arrow head and label background; "color"
                // drives the label text. Missing/undefined styling falls back to the
                // theme CSS variable.
                const overrideBgColor = r.styling?.["background-color"];
                const overrideTextColor = r.styling?.["color"];
                const edgeColor =
                    overrideBgColor || "var(--colors-sd-system-color-impression-primary)";

                edge.type = "deletable";
                edge.animated = true;
                edge.style = this.withEdgeShadow({ stroke: edgeColor, strokeWidth: 5, strokeDasharray: "5,5" });
                edge.data = {
                    ...(edge.data ?? {}),
                    onDelete: this.deleteEdge,
                    labelColor: overrideTextColor,
                    labelBgColor: overrideBgColor,
                };
                edge.markerEnd = { ...this.deletableMarkerEnd, color: edgeColor };
                edge.sourceHandle = "l-source";
                edge.targetHandle = "r-target";
                floatingEdges.push(edge);
            }
        });
        const { nodes: layoutedNodes } = this.getLayoutedElements(
            initialNodes,
            treeEdges,
        );
        const mergedNodes = this.reconcileNodesWithCurrent(layoutedNodes);
        const mergedEdges = this.reconcileEdgesWithCurrent([
            ...treeEdges,
            ...floatingEdges,
        ]);

        if (!this.storedGraphState)
            this.storeGraphState({
                nodes: mergedNodes,
                edges: this.toExportableEdges(mergedEdges),
                viewport: this.reactFlowInstance?.getViewport(),
            });

        this.setState(
            {
                nodes: mergedNodes,
                edges: mergedEdges,
                isDirty: false,
            },
            () =>
            {
                this.suppressDirtyTracking = false;
            },
        );
    };

    private reconcileNodesWithCurrent = (downloadedNodes: RFNode[]): RFNode[] =>
    {
        const currentNodeMap = new Map(
            this.state.nodes.map((node) => [node.id, node]),
        );

        // Dagre re-lays out the whole graph in its own coordinate space on every sync,
        // but existing nodes are pinned back to their current (user-arranged / restored)
        // positions below. A brand-new node would otherwise keep its raw dagre coordinate,
        // which has no relationship to where the existing nodes actually sit — so it can
        // land right on top of one of them. resolveNewNodePosition translates the new
        // node's dagre position into the existing nodes' real coordinate space by anchoring
        // it to the nearest existing node and reapplying dagre's relative offset, then nudges
        // it clear of anything it still overlaps.
        const existingAnchors = downloadedNodes.flatMap((node) =>
        {
            const current = currentNodeMap.get(node.id);
            return current
                ? [{ current: current.position, dagre: node.position }]
                : [];
        });
        const occupied = existingAnchors.map((anchor) => anchor.current);
        const overlaps = (
            a: { x: number; y: number },
            b: { x: number; y: number },
        ): boolean =>
            Math.abs(a.x - b.x) < this.nodeWidth + 40 &&
            Math.abs(a.y - b.y) < this.nodeHeight + 40;
        const resolveNewNodePosition = (dagrePos: { x: number; y: number }) =>
        {
            let position = dagrePos;
            if (existingAnchors.length > 0)
            {
                const nearest = existingAnchors.reduce((closest, anchor) =>
                {
                    const dx = anchor.dagre.x - dagrePos.x;
                    const dy = anchor.dagre.y - dagrePos.y;
                    const distSq = dx * dx + dy * dy;
                    return distSq < closest.distSq ? { anchor, distSq } : closest;
                }, { anchor: existingAnchors[0], distSq: Number.POSITIVE_INFINITY }).anchor;

                position = {
                    x: nearest.current.x + (dagrePos.x - nearest.dagre.x),
                    y: nearest.current.y + (dagrePos.y - nearest.dagre.y),
                };
            }

            let guard = 0;
            let collides = occupied.some((o) => overlaps(position, o));
            while (collides && guard < 500)
            {
                position = { x: position.x + this.nodeWidth + 40, y: position.y };
                const candidate = position;
                collides = occupied.some((o) => overlaps(candidate, o));
                guard++;
            }
            occupied.push(position);
            return position;
        };

        return downloadedNodes.map((downloadedNode) =>
        {
            const currentNode = currentNodeMap.get(downloadedNode.id);
            if (!currentNode)
            {
                return {
                    ...downloadedNode,
                    position: resolveNewNodePosition(downloadedNode.position),
                };
            }

            const downloadedData = (downloadedNode.data ?? {}) as IGraphNodeData;
            const currentData = (currentNode.data ?? {}) as IGraphNodeData;

            return {
                ...downloadedNode,
                position: currentNode.position ?? downloadedNode.position,
                selected: currentNode.selected,
                data: {
                    ...downloadedData,
                    isExpanded:
                        currentData.isExpanded ?? downloadedData.isExpanded ?? true,
                    onToggleExpanded: this.handleToggleNodeExpanded,
                },
            };
        });
    };

    private reconcileEdgesWithCurrent = (downloadedEdges: RFEdge[]): RFEdge[] =>
    {
        const currentEdgeMap = new Map(
            this.state.edges.map((edge) => [edge.id, edge]),
        );

        return downloadedEdges.map((downloadedEdge) =>
        {
            const currentEdge = currentEdgeMap.get(downloadedEdge.id);
            const hasDeleteAction = downloadedEdge.type === "deletable";

            return {
                ...downloadedEdge,
                style: this.withEdgeShadow(downloadedEdge.style as React.CSSProperties | undefined),
                selected: currentEdge?.selected,
                data: {
                    ...(downloadedEdge.data ?? {}),
                    onDelete: hasDeleteAction ? this.deleteEdge : undefined,
                },
            };
        });
    };

    private onNodesChange: OnNodesChange = (changes) =>
    {
        const shouldMarkDirty =
            !this.suppressDirtyTracking && this.hasMeaningfulNodeChanges(changes);

        this.setState((prevState) => ({
            nodes: applyNodeChanges(changes, prevState.nodes),
            isDirty: shouldMarkDirty ? true : prevState.isDirty,
        }));
    };

    private onEdgesChange: OnEdgesChange = (changes) =>
    {
        // Edge add/remove is persisted server-side via onAddRelationship / onRemoveRelationship,
        // so it does not contribute to the "dirty" (unsaved layout) state.
        this.setState((prevState) => ({
            edges: applyEdgeChanges(changes, prevState.edges),
        }));
    };

    private onConnectStart: OnConnectStart = (_event, params) =>
    {
        this.connectStart = { nodeId: params.nodeId, handleType: params.handleType };
    };

    // ReactFlow calls this during the drag — returning false suppresses the drop and
    // prevents the user from connecting a node to itself.
    private isValidConnection = (connection: Connection | RFEdge): boolean =>
    {
        return connection.source !== connection.target;
    };

    private onConnect: OnConnect = (connection: Connection) =>
    {
        if (this.isInteractionBlocked)
        {
            return;
        }

        // Each node exposes both a source and a target handle per side, so ReactFlow's
        // own source/target assignment (which is driven by handle type, not drag origin)
        // would reverse the relationship when the user drags from a target handle. Swap
        // here so the drag origin is always the source of the new relationship.
        const startedFromTarget = this.connectStart?.handleType === 'target';
        this.connectStart = undefined;

        const normalized: Connection = startedFromTarget
            ? {
                source: connection.target,
                target: connection.source,
                sourceHandle: connection.targetHandle,
                targetHandle: connection.sourceHandle,
            }
            : connection;

        // Defense in depth — isValidConnection should already have stopped the drop,
        // but block self-loops here too in case ReactFlow ever invokes onConnect without it.
        if (normalized.source === normalized.target)
        {
            return;
        }

        const source = this.props.nodes.find(
            (n) => n.id.toString() === normalized.source,
        );
        const target = this.props.nodes.find(
            (n) => n.id.toString() === normalized.target,
        );
        if (!source || !target) return;
        this.props.onAddRelationship(source, target).then((r) =>
        {
            if (r)
            {
                let name = this.languageValuesHelper.getValue(r.displayName);
                if (!name) name = r.name;
                // Mirror syncAndLayout: default to the theme colour but let the new
                // relationship's styling override the edge body, arrow head and label.
                const overrideBgColor = r.styling?.["background-color"];
                const overrideTextColor = r.styling?.["color"];
                const edgeColor =
                    overrideBgColor || "var(--colors-sd-system-color-impression-primary)";
                this.setState((prevState) => ({
                    edges: addEdge(
                        {
                            ...normalized,
                            type: "deletable",
                            animated: true,
                            style: this.withEdgeShadow({
                                stroke: edgeColor,
                                strokeWidth: 5,
                                strokeDasharray: "5,5",
                            }),
                            markerEnd: { ...this.deletableMarkerEnd, color: edgeColor },
                            sourceHandle: "l-source",
                            targetHandle: "r-target",
                            data: {
                                label: name,
                                onDelete: this.deleteEdge,
                                labelColor: overrideTextColor,
                                labelBgColor: overrideBgColor,
                            },
                        },
                        prevState.edges,
                    ),
                }), this.autoSaveLayoutIfClean);
            }
        });
    };

    private onSelectionChange = (params: {
        nodes: RFNode[];
        edges: RFEdge[];
    }) =>
    {

        if (params.nodes.length > 0)
        {
            try
            {
                this.props.onSelectedNodeChange?.(Number.parseInt(params.nodes[0].id));
                return;
            }
            catch
            {
                // default to undefined
            }
        }
        this.props.onSelectedNodeChange?.(undefined);
    };

    private deleteEdge = (edgeId: string) =>
    {
        if (this.isInteractionBlocked)
        {
            return;
        }

        const edge = this.props.edges.find((r) => r.id === edgeId);
        if (!edge || !edge.source || !edge.destination)
        {
            return;
        }

        this.props
            .onRemoveRelationship(
                edge.relationshipId,
                edge.source,
                edge.destination,
            )
            .then((result) =>
            {
                if (result)
                    this.setState((prevState) => ({
                        edges: prevState.edges.filter((e) => e.id !== edgeId),
                    }), this.autoSaveLayoutIfClean);
            });
    };

    private handleToggleNodeExpanded = (nodeId: string): void =>
    {
        this.setState((prevState) => ({
            nodes: prevState.nodes.map((node) =>
            {
                if (node.id !== nodeId)
                {
                    return node;
                }

                const nodeData = (node.data ?? {}) as IGraphNodeData;
                return {
                    ...node,
                    data: {
                        ...nodeData,
                        isExpanded: !(nodeData.isExpanded ?? true),
                        onToggleExpanded: this.handleToggleNodeExpanded,
                    },
                };
            }),
            isDirty: this.suppressDirtyTracking ? prevState.isDirty : true,
        }));
    };

    private hydrateRestoredNodes(nodes: RFNode[]): RFNode[]
    {
        // Save/restore is not allowed to alter the live selection. Carry over the
        // `selected` flag from the node that's currently in state with the same id,
        // so a restore leaves the selected node selected (and anything that wasn't
        // is still not selected). Nodes new to the restored set default to false.
        const selectedById = new Map(
            this.state.nodes.map(n => [n.id, n.selected ?? false]),
        );
        return nodes.map((node) => ({
            ...node,
            selected: selectedById.get(node.id) ?? false,
            data: {
                ...(node.data ?? {}),
                isEnabled: ((node.data ?? {}) as IGraphNodeData).isEnabled,
                isExpanded: ((node.data ?? {}) as IGraphNodeData).isExpanded ?? true,
                onToggleExpanded: this.handleToggleNodeExpanded,
            },
        }));
    }

    private hydrateRestoredEdges(edges: RFEdge[]): RFEdge[]
    {
        return edges.map((edge) => ({
            ...edge,
            markerEnd: edge.type === "smoothstep"
                ? { ...this.hierarchicalMarkerEnd }
                : { ...this.deletableMarkerEnd },
            style: this.withEdgeShadow(edge.style as React.CSSProperties | undefined),
            data: {
                ...(edge.data ?? {}),
                onDelete: edge.type === "deletable" ? this.deleteEdge : undefined,
            },
        }));
    }

    private handleSave = (): void =>
    {
        if (this.isInteractionBlocked)
        {
            return;
        }

        if (!this.reactFlowInstance)
        {
            return;
        }

        const graphState = this.reactFlowInstance.toObject();
        this.storeGraphState({
            ...graphState,
            edges: this.toExportableEdges(graphState.edges),
        });
        this.setState({ isDirty: false });
    };

    private handleRestore = (): void =>
    {
        if (this.isInteractionBlocked)
        {
            return;
        }

        this.restoreGraph(false);
    };

    private restoreGraph = (skipDirtyConfirmation: boolean): void =>
    {
        if (!this.reactFlowInstance)
        {
            return;
        }

        if (!skipDirtyConfirmation && this.state.isDirty)
        {
            const shouldRestore = window.confirm(
                this.labels.Plaftform_Warn_UnsavedChanges,
            );
            if (!shouldRestore)
            {
                return;
            }
        }

        const restoredState = this.readStoredGraphState();
        if (!restoredState)
        {
            return;
        }

        try
        {
            const restoredNodes = this.hydrateRestoredNodes(
                restoredState.nodes ?? [],
            );
            // Stored layout only contains hierarchical edges; non-hierarchical (deletable)
            // edges are kept from the current state since they come from props, not from
            // the persisted layout.
            const restoredEdges = this.hydrateRestoredEdges(
                restoredState.edges ?? [],
            );
            const currentDeletableEdges = this.state.edges.filter(
                (e) => e.type === "deletable",
            );

            this.suppressDirtyTracking = true;

            this.setState(
                {
                    nodes: restoredNodes,
                    edges: [...restoredEdges, ...currentDeletableEdges],
                    isDirty: false,
                },
                () =>
                {
                    this.suppressDirtyTracking = false;
                },
            );

            // Restore mustn't programmatically change selection — except when the
            // previously-selected node was pruned by this restore (e.g. a node that
            // was added since the layout was last saved). In that case the parent's
            // selectedNodeId would dangle, so clear it.
            const previouslySelected = this.state.nodes.find(n => n.selected);
            if (previouslySelected)
            {
                const stillPresent = restoredNodes.some(n => n.id === previouslySelected.id);
                if (!stillPresent)
                {
                    this.props.onSelectedNodeChange?.(undefined);
                }
            }

            if (restoredState.viewport)
            {
                this.reactFlowInstance.setViewport(restoredState.viewport);
            }
        } catch {
            // Ignore malformed local storage payloads.
        }
    };

    public render(): JSX.Element
    {
        return (
              <div className="graph-view">
                <DirtyPageObserver
                    pageKey="admin-nodes-graph-view"
                    isDirty={this.state.isDirty}
                />
                {!this.props.refreshToken && (
                    <div className="graph-view__loading">
                        <ProgressIndicatorIndeterminate
                            type="circular"
                            size="medium"
                            color="primary"
                        />
                        <span>{this.labels.Platform_Graph_Loading}</span>
                    </div>
                )}
                <ReactFlow
                    nodes={this.state.nodes}
                    edges={this.state.edges}
                    onNodesChange={this.onNodesChange}
                    onEdgesChange={this.onEdgesChange}
                    onConnectStart={this.onConnectStart}
                    onConnect={this.onConnect}
                    isValidConnection={this.isValidConnection}
                    onSelectionChange={this.onSelectionChange}
                    nodesConnectable={!!this.props.refreshToken}
                    onInit={(instance) =>
                    {
                        this.reactFlowInstance = instance;
                        this.restoreGraph(true);
                    }}
                    nodeTypes={this.nodeTypes}
                    edgeTypes={this.edgeTypes}
                    minZoom={0.1}
                    fitView
                >
                    <Background gap={20} color="#e2e2e2" />

                    <Panel position="top-right" className="graph-view__panel">
                        <Button
                            styleType="filled"
                            disabled={this.isInteractionBlocked || !this.state.isDirty}
                            onClick={() => this.handleSave()}
                        >
                            {this.labels.Platform_CommonAction_Save}
                        </Button>
                        <Button
                            styleType="filled"
                            disabled={
                                this.isInteractionBlocked ||
                                !this.state.isDirty ||
                                !this.props.storedLayout
                            }
                            onClick={() => this.handleRestore()}
                        >
                            {this.labels.Platform_CommonAction_Restore}
                        </Button>
                    </Panel>
                    <Controls className="graph-view__controls" />
                    <MiniMap
                        nodeColor={(n) => (n.data?.isEnabled ? "var(--colors-sd-system-color-impression-primary-container)" : "var(--colors-sd-system-color-interaction-disabled-on-surface")}
                        maskColor="rgba(0, 0, 0, 0.1)"
                    />
                </ReactFlow>
            </div>
        );
    }
}

export interface IGraphViewProps
{
    rootNodeId: string;
    nodes: IGraphNode[];
    edges: IGraphEdge[];
    storedLayout: string;
    refreshToken: string;
    // Parent-level cache of every ontology node referenced by any IGraphNode in
    // `nodes`. Nodes carry just IDs; GraphView looks each one up here when it
    // needs the name + styling for rendering and colour assignment.
    ontologyNodes: IOntologyNode[];
    onAddRelationship: ( source: IGraphNode, target: IGraphNode) => Promise<IGraphEdge | undefined>;
    onRemoveRelationship: (id: string, source: IGraphNode, target: IGraphNode) => Promise<boolean>;
    onSaveLayout: (layout: string) => void;
    onSelectedNodeChange?: (nodeId: number | undefined) => void;
}

export interface IGraphViewState
{
    nodes: RFNode[];
    edges: RFEdge[];
    isDirty: boolean;
}

export interface IGraphNode
{
    id: number;
    children?: IGraphNode[];
    parentId: number;
    name: string;
    displayName: ILanguageValues;
    isEnabled: boolean;
    // Reference into the parent-level ontologyNodes cache passed through to
    // GraphView as a prop. Keeping just the IDs here avoids duplicating the
    // ontology data per node and lets us resolve styling from a single source.
    ontologyNodeIds: string[];
    solutionNames: string[];
}

export interface IOntologyNode
{
    id: string;
    name: string;
    styling: { [key: string]: string };
}

export interface IGraphEdge
{
    id: string;
    name: string;
    relationshipId: string;
    displayName: ILanguageValues;
    hierarchical: boolean;
    source?: IGraphNode;
    destination?: IGraphNode;
    styling?: { [key: string]: string };
}

export interface IGraphNodeData
{
    [key: string]: unknown;
    label: string;
    description: string;
    isEnabled: boolean;
    headerColor: string;
    bgColor: string;
    ontologyNodes: IOntologyNode[];
    solutions: string[];
    isExpanded: boolean;
    onToggleExpanded?: (nodeId: string) => void;
}

export interface IStoredGraphState
{
    nodes?: RFNode[];
    edges?: RFEdge[];
    viewport?: { x: number; y: number; zoom: number };
}
